// @ts-nocheck
import { createSlice } from '@reduxjs/toolkit';
import {
  getSelectedAddOnPassengerIds,
  matchesId
} from '../../../utils/percentageAddOns';

const emptyPlan = {
  tripplanid: null,
  title: '',
  description: '',
  priceperperson: 0,
  occupancy: 0,
  maxavailable: null,
  depositamount: 0,
  depositduedate: '',
  lastbookdate: '',
  finalpaymentduedate: '',
  selectedQuantity: 0
};

const initialState = {
  bookingplans: [emptyPlan],
  passengerForms: [],
  tenant: {},
  trip: {},
  result: {},
  addons: [], // Trip add-ons available for selection
  selectedAddOns: [], // Selected add-ons with passenger assignments: [{ tripaddonid, passengerFormIds: [] }]
  appliedDiscountCodes: [],
  paymentOption: null,
  amountToCharge: null, // Will be calculated when booking plans are loaded
  tripCapacity: null, // Total trip capacity (null means unlimited)
  usedCapacity: 0, // Passengers already booked on this trip
  steps: [
    {
      id: 1,
      active: true,
      title: 'Choose a plan',
      key: 'plan',
      shouldRender: true,
      message: ''
    },
    {
      id: 2,
      active: false,
      title: 'Passengers',
      key: 'passenger',
      shouldRender: true,
      message: ''
    },
    {
      id: 3,
      active: false,
      title: 'Add-ons',
      key: 'addons',
      shouldRender: false, // Will be set to true conditionally based on enableAddOns flag and available add-ons
      message: ''
    },
    {
      id: 4,
      active: false,
      title: 'Complete payment',
      key: 'payment',
      shouldRender: true,
      message: ''
    },
    {
      id: 5,
      active: false,
      title: 'Payment successful',
      key: 'success',
      shouldRender: false,
      message: ''
    }
  ],
  bookingTotals: []
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState,
  reducers: {
    addBooking: (state, action) => {
      const {
        bookingplans,
        tenant,
        trip,
        usedCapacity,
        addons,
        isFullCapacity
      } = action.payload;

      const newState = {
        ...state,
        bookingplans: bookingplans || [],
        tenant,
        trip,
        addons: addons || [],
        appliedDiscountCodes: [],
        amountToCharge: null,
        tripCapacity: trip?.capacity ?? null,
        usedCapacity: usedCapacity ?? 0,
        isFullCapacity
      };

      return newState;
    },
    setUniqueSelectedQuantity: (state, action) => {
      const { tripplanid, quantity } = action.payload;

      const newBookingPlans = state.bookingplans.map((bookingplan) => {
        if (bookingplan.tripplanid === tripplanid) {
          return {
            ...bookingplan,
            selectedQuantity: quantity,
            isSelected: true
          };
        } else {
          return { ...bookingplan, selectedQuantity: 0, isSelected: false };
        }
      });

      return {
        ...state,
        bookingplans: newBookingPlans,
        appliedDiscountCodes: [],
        amountToCharge: null // Reset to trigger recalculation
      };
    },

    setSelectedQuantity: (state, action) => {
      const { tripplanid, quantity } = action.payload;

      const newBookingPlans = state.bookingplans.map((bookingplan) => {
        if (bookingplan.tripplanid === tripplanid) {
          return { ...bookingplan, selectedQuantity: quantity };
        } else {
          return { ...bookingplan };
        }
      });

      return {
        ...state,
        bookingplans: newBookingPlans,
        appliedDiscountCodes: [],
        amountToCharge: null // Reset to trigger recalculation
      };
    },
    updateActiveStep: (state, action) => {
      const { key, message = '' } = action.payload;

      const newBookingSteps = state.steps.map((step) => {
        if (step.key === key) {
          return {
            ...step,
            active: true,
            message
          };
        } else {
          return {
            ...step,
            active: false,
            message: ''
          };
        }
      });

      return {
        ...state,
        steps: newBookingSteps
      };
    },
    clearBooking: () => {
      return initialState;
    },
    updateTripCapacity: (state, action) => {
      const { capacity } = action.payload;
      return {
        ...state,
        tripCapacity: capacity
      };
    },
    updateBookingResult: (state, action) => {
      const { result } = action.payload;
      return {
        ...state,
        result
      };
    },

    updatePassengerForms: (state, action) => {
      const { passengerForms } = action.payload;

      // Get valid formIds from the new passenger forms
      const validFormIds = passengerForms.flatMap((form) => [
        form.formId,
        form.id
      ]);

      // Clean up selectedAddOns - remove invalid passenger references
      const cleanedSelectedAddOns = state.selectedAddOns
        .map((addon) => ({
          ...addon,
          passengerFormIds: addon.passengerFormIds.filter((id) =>
            validFormIds.some((validId) => matchesId(validId, id))
          )
        }))
        .filter((addon) => addon.passengerFormIds.length > 0); // Remove add-ons with no passengers left

      return {
        ...state,
        passengerForms,
        selectedAddOns: cleanedSelectedAddOns,
        appliedDiscountCodes: [],
        amountToCharge: null
      };
    },

    updatePassengerField: (state, action) => {
      const { formId, fieldId, newValue, error = null } = action.payload;

      const newPassengerForms = state.passengerForms.map(
        (passengerForm = {}) => {
          if (passengerForm?.formId === formId) {
            return {
              ...passengerForm,

              fields: passengerForm?.fields?.map((field) => {
                if (field.fieldId === fieldId) {
                  return {
                    ...field,
                    value: newValue,
                    error
                  };
                } else {
                  return field;
                }
              })
            };
          } else {
            return passengerForm;
          }
        }
      );

      return {
        ...state,
        passengerForms: newPassengerForms
      };
    },

    updateFormErrors: (state, action) => {
      const { forms = [] } = action.payload;

      const newPassengerForms = state.passengerForms.map(
        (passengerForm = {}) => {
          const foundForm = forms?.find(
            (form) => form?.formId === passengerForm?.formId
          );

          if (foundForm) {
            const errors = foundForm?.errors || [];

            return {
              ...passengerForm,
              fields: passengerForm?.fields?.map((field) => {
                const foundError = errors?.find(
                  (error) => error?.fieldId === field?.fieldId
                );
                return {
                  ...field,
                  error: foundError?.error
                };
              })
            };
          } else {
            return passengerForm;
          }
        }
      );

      return {
        ...state,
        passengerForms: newPassengerForms
      };
    },

    resetFieldErrors: (state) => {
      const newPassengerForms = state.passengerForms.map(
        (passengerForm = {}) => {
          return {
            ...passengerForm,
            fields: passengerForm?.fields?.map((field) => {
              return {
                ...field,
                error: null
              };
            })
          };
        }
      );
      return {
        ...state,
        passengerForms: newPassengerForms
      };
    },

    ///Totals
    removeTotalItem: (state, action) => {
      const { totalKey } = action.payload;
      const newTotals = state.bookingTotals.filter(
        (total) => total.key !== totalKey
      );
      return {
        ...state,
        bookingTotals: newTotals
      };
    },
    addTotalItem: (state, action) => {
      const { totalItem } = action.payload;

      return {
        ...state,
        bookingTotals: [...state.bookingTotals, totalItem]
      };
    },
    addTotalItems: (state, action) => {
      const { totalItems = [] } = action.payload;

      return {
        ...state,
        bookingTotals: totalItems
      };
    },

    updateTotalItem: (state, action) => {
      const { totalKey, totalItem } = action.payload;

      const newTotals = state.bookingTotals.map((total) => {
        if (total.key === totalKey) {
          return { ...total, ...totalItem };
        } else {
          return total;
        }
      });
      return {
        ...state,
        bookingTotals: newTotals
      };
    },
    resetTotals: (state) => {
      return {
        ...state,
        bookingTotals: []
      };
    },
    resetPricingSelection: (state) => {
      const newBookingPlans = state.bookingplans.map((bookingplan) => {
        return {
          ...bookingplan,
          selectedQuantity: 0,
          isSelected: false
        };
      });

      return {
        ...state,
        bookingplans: newBookingPlans,
        appliedDiscountCodes: [],
        amountToCharge: null, // Reset to trigger recalculation
        paymentOption: null, // Reset payment option as well
        bookingTotals: [] // Clear any calculated totals
      };
    },
    updatePaymentOption: (state, action) => {
      const { paymentOption, amountToCharge } = action.payload;
      return {
        ...state,
        paymentOption,
        amountToCharge:
          amountToCharge !== undefined ? amountToCharge : state.amountToCharge
      };
    },
    addAppliedDiscountCode: (state, action) => {
      const discountCode = action.payload;
      if (!discountCode?.discountCodeId) return state;

      const exists = state.appliedDiscountCodes.some(
        (code) => code.discountCodeId === discountCode.discountCodeId
      );

      if (exists) return state;

      return {
        ...state,
        appliedDiscountCodes: [...state.appliedDiscountCodes, discountCode],
        amountToCharge: null
      };
    },
    removeAppliedDiscountCode: (state, action) => {
      const { discountCodeId } = action.payload;

      return {
        ...state,
        appliedDiscountCodes: state.appliedDiscountCodes.filter(
          (code) => code.discountCodeId !== discountCodeId
        ),
        amountToCharge: null
      };
    },
    clearAppliedDiscountCodes: (state) => {
      return {
        ...state,
        appliedDiscountCodes: [],
        amountToCharge: null
      };
    },
    updateAddOnsStepVisibility: (state, action) => {
      const { shouldRender } = action.payload;
      const newSteps = state.steps.map((step) => {
        if (step.key === 'addons') {
          return {
            ...step,
            shouldRender
          };
        }
        return step;
      });
      return {
        ...state,
        steps: newSteps
      };
    },
    toggleAddOnForPassenger: (state, action) => {
      const { tripaddonid, passengerFormId } = action.payload;
      const existingIndex = state.selectedAddOns.findIndex((addon) =>
        matchesId(addon.tripaddonid, tripaddonid)
      );
      const passengerIds = getSelectedAddOnPassengerIds({
        selectedAddOns: state.selectedAddOns,
        tripaddonid
      });
      const isSelected = passengerIds.some((id) =>
        matchesId(id, passengerFormId)
      );
      const nextPassengerIds = isSelected
        ? passengerIds.filter((id) => !matchesId(id, passengerFormId))
        : [...passengerIds, passengerFormId];
      const nextAddOn = {
        ...state.selectedAddOns[existingIndex],
        tripaddonid,
        passengerFormIds: nextPassengerIds
      };
      // Replace all equivalent rows once, including legacy mixed-type duplicates.
      const selectedAddOns = state.selectedAddOns.flatMap((addon, index) => {
        if (!matchesId(addon.tripaddonid, tripaddonid)) return [addon];
        return index === existingIndex && nextPassengerIds.length > 0
          ? [nextAddOn]
          : [];
      });
      if (existingIndex === -1) selectedAddOns.push(nextAddOn);

      return {
        ...state,
        selectedAddOns,
        appliedDiscountCodes: [],
        amountToCharge: null
      };
    }
  }
});

const { actions, reducer } = bookingSlice;

export const {
  addBooking,
  clearBooking,
  setSelectedQuantity,
  setUniqueSelectedQuantity,
  updateActiveStep,
  updateBookingResult,
  updateTripCapacity,
  updatePassengerForms,
  updatePassengerField,
  resetFieldErrors,
  updateFormErrors,
  removeTotalItem,
  addTotalItem,
  addTotalItems,
  updateTotalItem,
  resetTotals,
  resetPricingSelection,
  updatePaymentOption,
  addAppliedDiscountCode,
  removeAppliedDiscountCode,
  clearAppliedDiscountCodes,
  updateAddOnsStepVisibility,
  toggleAddOnForPassenger
} = actions;

export default reducer;
